import React, { useState, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown } from '../../shared/components/Icons';
import { InfoTooltip } from '../../shared/components/InfoTooltip';
import { confColor, Badge } from './confColorUtils';
import { cleanSubtopicName } from '../../shared/utils/topicDisplay';
import TopicDetailPanel from './TopicDetailPanel';

/**
 * Compact topic row used in the detailed-subtopics (L3) view.
 *   - Header: chevron + name + estimated badge + confidence bar + chevron
 *   - When opened: renders the standalone TopicDetailPanel for sections A-I
 *
 * The detail panel was extracted so the same component can be used by the
 * L3Row drill-in inside a study unit, where wrapping the detail in a full
 * TopicRow shows the row's own chrome (Moderate badge, slider) twice.
 */
function TopicRow({ topic, onSave, forceOpen, moduleColor, isFocused, statuses, tourHighlight, sraStat, onNavigateToSRA }) {
  const [open, setOpen] = useState(false);
  const rowRef = useRef(null);

  useEffect(() => {
    if (isFocused) {
      setOpen(true);
      setTimeout(() => {
        if (rowRef.current) {
          const top = rowRef.current.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [isFocused]);

  useEffect(() => { if (forceOpen !== null) setOpen(forceOpen); }, [forceOpen]);

  const focusRing = isFocused ? { boxShadow: `0 0 0 2px ${moduleColor}`, borderColor: moduleColor } : {};
  const displayName = cleanSubtopicName(topic.display_name || topic.subtopic);

  return (
    <div ref={rowRef} className={`topic-row ${open ? 'open' : ''} ${topic.is_estimated ? 'is-estimated' : ''}`} style={focusRing}>
      <div className="topic-header" onClick={() => setOpen(o => !o)}>
        <div className="topic-info">
          <div className="topic-name">
            {displayName}
            {topic.is_estimated && (
              <span className="estimated-badge" title="Estimated from subject-area rating">
                est.<InfoTooltip text="This confidence was estimated from your MCQ score on a broader topic area. Rate it yourself for a more accurate picture." />
              </span>
            )}
          </div>
        </div>
        <div className="topic-right">
          <Badge c={topic.confidence} status={topic.status} />
          <div className="conf-wrap">
            <div className="conf-bar-bg">
              <div
                className="conf-bar-fill"
                style={{
                  width: (topic.confidence !== null ? Math.round(topic.confidence * 100) : 0) + '%',
                  background: confColor(topic.confidence),
                }}
              />
            </div>
            <span className="conf-pct" style={{ color: confColor(topic.confidence) }}>
              {topic.confidence !== null ? Math.round(topic.confidence * 100) + '%' : '—'}
            </span>
          </div>
          <span className="chevron">{open ? <ChevronUp size={10} /> : <ChevronDown size={10} />}</span>
        </div>
      </div>

      {open && (
        <TopicDetailPanel
          topic={topic}
          onSave={onSave}
          statuses={statuses}
          onNavigateToSRA={onNavigateToSRA}
        />
      )}
    </div>
  );
}

export default TopicRow;
